/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useEffect, useMemo } from "react";
import { authApi } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    // Experimental identity-management features remain isolated in branch T10.
    const experimentalIdentityFeaturesEnabled = false;
    const [user, setUser] = useState(() => {
        try {
            const saved = localStorage.getItem("user");
            return saved ? JSON.parse(saved) : null;
        } catch {
            return null;
        }
    });
    const [token, setToken] = useState(() => localStorage.getItem("token"));
    const [roles, setRoles] = useState(() => {
        try {
            const saved = localStorage.getItem("roles");
            return saved ? JSON.parse(saved) : [];
        } catch {
            return [];
        }
    });
    const [loading, setLoading] = useState(true);
    const [permissionEditMode, setPermissionEditMode] = useState(() =>
        experimentalIdentityFeaturesEnabled &&
        localStorage.getItem("permission_edit_mode") === "true"
    );
    const [impersonatedBy, setImpersonatedBy] = useState(() => {
        if (!experimentalIdentityFeaturesEnabled) {
            return null;
        }
        try {
            const saved = localStorage.getItem("impersonated_by");
            return saved ? JSON.parse(saved) : null;
        } catch {
            return null;
        }
    });

    const setPermissionEditModePersisted = (enabled) => {
        setPermissionEditMode(enabled);
        localStorage.setItem("permission_edit_mode", String(enabled));
    };

    const allRoleTags = useMemo(() => {
        const fromRoles = Array.isArray(roles)
            ? roles.map((r) =>
                  (typeof r === "string"
                      ? r
                      : r?.tag || r?.name || ""
                  ).toLowerCase()
              )
            : [];
        const fromUser = Array.isArray(user?.roles)
            ? user.roles.map((r) =>
                  (typeof r === "string"
                      ? r
                      : r?.tag || r?.name || ""
                  ).toLowerCase()
              )
            : [];
        return [...new Set([...fromRoles, ...fromUser])];
    }, [roles, user?.roles]);

    const isAdmin =
        allRoleTags.includes("root") ||
        allRoleTags.includes("admin") ||
        Boolean(user?.is_admin) ||
        Boolean(user?.is_root);
    const isRoot =
        allRoleTags.includes("root") || Boolean(user?.is_root) || isAdmin;
    const isIt = allRoleTags.includes("it") || isAdmin;
    const isDevelopment = allRoleTags.includes("development") || isIt;
    const isClient =
        allRoleTags.includes("client") &&
        !isAdmin &&
        !allRoleTags.includes("it") &&
        !allRoleTags.includes("development");
    const hasTasks = isIt;
    const canChangePermissions =
        experimentalIdentityFeaturesEnabled && (isRoot || isIt);

    const hostname =
        typeof window !== "undefined" ? window.location.hostname : "localhost";
    const centralDomains = [
        "localhost",
        "127.0.0.1",
        "dev.victortormo.com",
        "certificacion.victortormo.com",
        "demoapp.victortormo.com",
        "app.victortormo.com",
    ];
    const isTenant = !centralDomains.includes(hostname);

    const handleLoginSuccess = (loginData) => {
        setUser(loginData.user);
        setToken(loginData.token);
        setRoles(loginData.roles || []);
        localStorage.setItem("token", loginData.token);
        localStorage.setItem("user", JSON.stringify(loginData.user));
        localStorage.setItem("roles", JSON.stringify(loginData.roles || []));
        if (loginData.device_token) {
            localStorage.setItem("device_token", loginData.device_token);
        }
    };

    const login = async (email, password) => {
        const res = await authApi.login({ email, password });
        if (res.result && res.data) {
            if (res.data.requires_2fa) {
                // Do not log the user in yet, just return the data so Login component can show 2FA form
                return res.data;
            }
            handleLoginSuccess(res.data);
            return res.data;
        }
        throw new Error(res.message || "Error en inicio de sesión");
    };

    const complete2fa = async (code, trustDevice, tempToken) => {
        const res = await authApi.twoFactorChallenge(
            { code, trust_device: trustDevice },
            tempToken
        );
        if (res.result && res.data) {
            handleLoginSuccess(res.data);
            return res.data;
        }
        throw new Error(res.message || "Código incorrecto");
    };

    const loginWithWebauthn = async (asseResp) => {
        const res = await authApi.webauthnLogin(asseResp);
        if (res.result && res.data) {
            handleLoginSuccess(res.data);
            return res.data;
        }
        throw new Error(res.message || "Error en inicio de sesión biométrico");
    };

    const logout = async () => {
        try {
            await authApi.logout();
        } catch {
            // ignore network errors on logout
        } finally {
            setUser(null);
            setToken(null);
            setRoles([]);
            localStorage.removeItem("token");
            localStorage.removeItem("user");
            localStorage.removeItem("roles");
        }
    };

    const impersonate = async (userId) => {
        const originalToken = localStorage.getItem("original_token") || token;
        if (!localStorage.getItem("original_token")) {
            localStorage.setItem("original_token", originalToken || "");
            localStorage.setItem("original_user", JSON.stringify(user));
            localStorage.setItem("original_roles", JSON.stringify(roles));
        }
        const res = await authApi.impersonate(userId);
        if (!res.result || !res.data) {
            throw new Error(res.message || "Impersonation failed");
        }
        handleLoginSuccess(res.data);
        setImpersonatedBy(res.data.impersonated_by);
        localStorage.setItem("impersonated_by", JSON.stringify(res.data.impersonated_by));
    };

    const stopImpersonation = () => {
        const originalToken = localStorage.getItem("original_token");
        const originalUser = localStorage.getItem("original_user");
        const originalRoles = localStorage.getItem("original_roles");
        if (!originalToken || !originalUser) return;
        setToken(originalToken);
        setUser(JSON.parse(originalUser));
        setRoles(originalRoles ? JSON.parse(originalRoles) : []);
        localStorage.setItem("token", originalToken);
        localStorage.setItem("user", originalUser);
        localStorage.setItem("roles", originalRoles || "[]");
        localStorage.removeItem("original_token");
        localStorage.removeItem("original_user");
        localStorage.removeItem("original_roles");
        localStorage.removeItem("impersonated_by");
        setImpersonatedBy(null);
    };

    const switchUser = async (email, password = "Vito123!") => {
        return await login(email, password);
    };

    useEffect(() => {
        const verifySession = async () => {
            if (token) {
                try {
                    const res = await authApi.me();
                    if (res.result && res.data) {
                        setUser(res.data.user);
                        setRoles(res.data.roles || []);
                        localStorage.setItem(
                            "user",
                            JSON.stringify(res.data.user)
                        );
                        localStorage.setItem(
                            "roles",
                            JSON.stringify(res.data.roles || [])
                        );
                    } else {
                        logout();
                    }
                } catch {
                    logout();
                }
            }
            setLoading(false);
        };
        verifySession();
    }, [token]);

    return (
        <AuthContext.Provider
            value={{
                user,
                setUser,
                token,
                roles,
                isAdmin,
                isRoot,
                canChangePermissions,
                permissionEditMode: canChangePermissions && permissionEditMode,
                setPermissionEditMode: setPermissionEditModePersisted,
                isIt,
                isDevelopment,
                isClient,
                hasTasks,
                isTenant,
                loading,
                login,
                loginWithWebauthn,
                complete2fa,
                logout,
                switchUser,
                impersonatedBy,
                impersonate,
                stopImpersonation,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useOptionalAuth = () => useContext(AuthContext);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};
